'use client';

import React, { useState, useRef, useEffect } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';
import { SearchIcon, CloseIcon, ArrowChevronIcon } from '../../../icons';
import { SelectModal } from './SelectModal';
import { SearchModal } from './SearchModal';

// ============================================
// useIsMobile Hook (Internal)
// ============================================
const useIsMobile = (breakpoint: number = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < breakpoint);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
};

// ============================================
// SearchFilterBar.TextSearch
// ============================================
type TextSearchProps = {
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  className?: string;
  showSearchButton?: boolean;
  showClearButton?: boolean;
  disabled?: boolean;
  responsive?: boolean;
};

const TextSearch: React.FC<TextSearchProps> = ({
  value = '',
  onChange,
  onSearch,
  placeholder = '검색어를 입력하세요',
  className,
  showSearchButton = true,
  showClearButton = true,
  disabled = false,
  responsive = true,
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const currentValue = onChange ? value : internalValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (onChange) {
      onChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(currentValue);
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange('');
    } else {
      setInternalValue('');
    }
    onSearch?.('');
    inputRef.current?.focus();
  };

  const handleSearch = () => {
    onSearch?.(currentValue);
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  return (
    <div
      onClick={handleContainerClick}
      className={cn(
        'group relative flex items-center rounded-md cursor-text',
        responsive
          ? 'h-8 pl-3 pr-2 md:h-9 md:pl-4 md:pr-1.5'
          : 'h-9 pl-4 pr-1.5',
        'bg-white border border-gscale-200',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      <SearchIcon className="flex-shrink-0 text-gscale-500 w-4 h-4" />
      <div className="relative flex-1 ml-2">
        <input
          ref={inputRef}
          type="text"
          value={currentValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full text-gscale-800 text-xs',
            'bg-transparent outline-none',
            'placeholder:text-gscale-500',
            disabled && 'cursor-not-allowed',
            showClearButton && currentValue && 'pr-6',
          )}
        />
        {showClearButton && currentValue && (
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className={cn(
              'absolute right-0 top-1/2 -translate-y-1/2',
              'p-1 rounded-full',
              'text-gscale-500 hover:text-gscale-700',
              'transition-colors duration-200',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <CloseIcon className="w-3 h-3" />
          </button>
        )}
      </div>
      {showSearchButton && (
        <button
          type="button"
          onClick={handleSearch}
          disabled={disabled}
          className={cn(
            'rounded font-medium ml-1',
            responsive
              ? 'hidden md:inline-flex items-center h-7 px-3 text-xs'
              : 'h-7 px-3 text-xs',
            'bg-gscale-100 text-gscale-800',
            'transition-colors duration-200',
            'hover:bg-gscale-200 hover:text-gscale-950',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          검색
        </button>
      )}
    </div>
  );
};

TextSearch.displayName = 'SearchFilterBar.TextSearch';

// ============================================
// SearchFilterBar.ModalSearch
// ============================================
type ModalSearchProps = {
  value?: string;
  displayValue?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  onModalOpen?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  responsive?: boolean;
  modalMaxWidth?: string;
  modalMaxHeight?: string;
  children?: React.ReactNode;
};

const ModalSearch: React.FC<ModalSearchProps> = ({
  value = '',
  displayValue,
  onChange,
  onSearch,
  onModalOpen,
  placeholder = '검색어를 입력하세요',
  className,
  disabled = false,
  responsive = true,
  modalMaxWidth = '500px',
  modalMaxHeight = '80vh',
  children,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  const currentValue = onChange ? value : internalValue;
  const triggerDisplayValue = displayValue ?? currentValue;

  const handleChange = (newValue: string) => {
    if (onChange) {
      onChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  const handleSearch = (searchValue: string) => {
    onSearch?.(searchValue);
  };

  const handleOpenModal = () => {
    if (disabled) return;
    onModalOpen?.();
    setModalOpen(true);
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleOpenModal}
        disabled={disabled}
        className={cn(
          'group relative flex items-center rounded-md cursor-pointer',
          responsive ? 'h-7 px-2.5 md:h-8 md:px-3' : 'h-8 px-3',
          'bg-gscale-50',
          'hover:bg-gscale-100 transition-colors duration-200',
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
      >
        <SearchIcon className="flex-shrink-0 text-gscale-500 w-4 h-4" />
        <span
          className={cn(
            'flex-1 ml-2 text-xs text-left truncate',
            triggerDisplayValue ? 'text-gscale-800' : 'text-gscale-500',
          )}
        >
          {triggerDisplayValue || placeholder}
        </span>
      </button>

      {/* Search Modal */}
      <SearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        value={currentValue}
        onChange={handleChange}
        onSearch={handleSearch}
        placeholder={placeholder}
        maxWidth={modalMaxWidth}
        maxHeight={modalMaxHeight}
      >
        {children}
      </SearchModal>
    </>
  );
};

ModalSearch.displayName = 'SearchFilterBar.ModalSearch';

// ============================================
// SearchFilterBar.DropdownSearch
// ============================================
type FilterOption = {
  value: string;
  label: string;
};

type DropdownSearchProps = {
  filters: FilterOption[];
  selectedFilter?: string;
  onFilterChange?: (filter: string) => void;
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (filter: string, value: string) => void;
  placeholder?: string;
  filterPlaceholder?: string;
  className?: string;
  showSearchButton?: boolean;
  showClearButton?: boolean;
  disabled?: boolean;
  filterWidth?: string;
  mobileFilterWidth?: string;
  responsive?: boolean;
};

const DropdownSearch: React.FC<DropdownSearchProps> = ({
  filters,
  selectedFilter,
  onFilterChange,
  value = '',
  onChange,
  onSearch,
  placeholder = '검색어를 입력하세요',
  filterPlaceholder = '필터 선택',
  className,
  showSearchButton = true,
  showClearButton = true,
  disabled = false,
  filterWidth = '110px',
  mobileFilterWidth = '90px',
  responsive = true,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [internalFilter, setInternalFilter] = useState(
    selectedFilter || filters[0]?.value || '',
  );
  const [internalValue, setInternalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentFilter = onFilterChange
    ? (selectedFilter ?? '')
    : internalFilter;
  const currentValue = onChange ? value : internalValue;

  const selectedLabel =
    filters.find(f => f.value === currentFilter)?.label || filterPlaceholder;

  const handleFilterChange = (newFilter: string) => {
    if (onFilterChange) {
      onFilterChange(newFilter);
    } else {
      setInternalFilter(newFilter);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (onChange) {
      onChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(currentFilter, currentValue);
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange('');
    } else {
      setInternalValue('');
    }
    onSearch?.(currentFilter, '');
    inputRef.current?.focus();
  };

  const handleSearch = () => {
    onSearch?.(currentFilter, currentValue);
  };

  const isMobile = useIsMobile();
  const effectiveFilterWidth =
    responsive && isMobile ? mobileFilterWidth : filterWidth;

  return (
    <>
      <div
        className={cn(
          'group relative flex items-center rounded-md overflow-hidden',
          responsive ? 'h-8 pr-1 md:h-9 md:pr-1.5' : 'h-9 pr-1.5',
          'bg-white border border-gscale-200',
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
      >
        {/* Filter Dropdown Trigger */}
        <button
          type="button"
          onClick={() => !disabled && setModalOpen(true)}
          disabled={disabled}
          className={cn(
            'flex items-center justify-between h-full',
            responsive ? 'px-2 md:px-3 text-xs' : 'px-3 text-xs',
            'font-medium text-gscale-800',
            'bg-transparent border-r border-gscale-200',
            disabled && 'cursor-not-allowed',
          )}
          style={{ width: effectiveFilterWidth }}
        >
          <span className="truncate">{selectedLabel}</span>
          <ArrowChevronIcon
            className={cn(
              'text-gscale-500 flex-shrink-0 ml-1 w-3.5 h-3.5',
              'transition-transform duration-200',
              modalOpen && 'rotate-180',
            )}
          />
        </button>

        {/* Search Input */}
        <div
          className={cn(
            'flex items-center flex-1 cursor-text',
            responsive ? 'px-2 md:px-3' : 'px-3',
          )}
          onClick={() => inputRef.current?.focus()}
        >
          <SearchIcon className="flex-shrink-0 text-gscale-500 w-4 h-4" />
          <input
            ref={inputRef}
            type="text"
            value={currentValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              'flex-1 ml-2 text-gscale-800 text-xs',
              responsive ? 'min-w-[80px] md:min-w-[120px]' : 'min-w-[120px]',
              'bg-transparent outline-none',
              'placeholder:text-gscale-500',
              disabled && 'cursor-not-allowed',
            )}
          />
          {showClearButton && currentValue && (
            <button
              type="button"
              onClick={handleClear}
              disabled={disabled}
              className={cn(
                'p-1 rounded-full',
                'text-gscale-500 hover:text-gscale-700',
                'transition-colors duration-200',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <CloseIcon className="w-3 h-3" />
            </button>
          )}
        </div>

        {showSearchButton && (
          <button
            type="button"
            onClick={handleSearch}
            disabled={disabled}
            className={cn(
              'rounded font-medium ml-1',
              responsive
                ? 'h-6 px-2 md:h-7 md:px-3 text-xs'
                : 'h-7 px-3 text-xs',
              'bg-gscale-100 text-gscale-800',
              'transition-colors duration-200',
              'hover:bg-gscale-200 hover:text-gscale-950',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            검색
          </button>
        )}
      </div>

      {/* Filter SelectModal */}
      <SelectModal
        title={filterPlaceholder}
        open={modalOpen}
        onOpenChange={setModalOpen}
        value={currentFilter}
        onChange={handleFilterChange}
      >
        {filters.map(filter => (
          <SelectModal.Option key={filter.value} value={filter.value}>
            {filter.label}
          </SelectModal.Option>
        ))}
      </SelectModal>
    </>
  );
};

DropdownSearch.displayName = 'SearchFilterBar.DropdownSearch';

// ============================================
// SearchFilterBar.Button
// ============================================
const buttonVariants = cva(
  'inline-flex items-center justify-center font-semibold flex-shrink-0 transition-all duration-200 ease-out',
  {
    variants: {
      variant: {
        normal: [
          'bg-gscale-100 text-gscale-800',
          'hover:bg-gscale-200 hover:text-gscale-950',
          'active:scale-[0.98]',
        ],
        primary: [
          'bg-gscale-900 text-white',
          'hover:bg-gscale-800 hover:shadow-lg hover:shadow-gscale-900/20',
          'active:scale-[0.98]',
        ],
        outline: [
          'border-2 border-gscale-200 text-gscale-700 bg-white',
          'hover:border-gscale-300 hover:bg-gscale-50',
          'active:scale-[0.98]',
        ],
        ghost: [
          'text-gscale-600',
          'hover:bg-gscale-100 hover:text-gscale-900',
          'active:scale-[0.98]',
        ],
      },
      size: {
        sm: 'h-8 px-3 md:h-9 md:px-4 text-xs rounded-md md:rounded-lg',
        md: 'h-10 px-3 md:h-11 md:px-5 text-xs md:text-sm rounded-lg md:rounded-xl',
        lg: 'h-11 px-4 md:h-12 md:px-6 text-xs md:text-sm rounded-lg md:rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'normal',
      size: 'md',
    },
  },
);

interface SearchFilterBarButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const SearchFilterBarButton: React.FC<SearchFilterBarButtonProps> = ({
  className,
  variant,
  size,
  disabled,
  children,
  ...props
}) => {
  return (
    <button
      type="button"
      className={cn(
        buttonVariants({ variant, size }),
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

SearchFilterBarButton.displayName = 'SearchFilterBar.Button';

// ============================================
// SearchFilterBar (Main Component)
// ============================================
const searchFilterBarVariants = cva('flex items-center w-full', {
  variants: {
    gap: {
      xs: 'gap-1',
      sm: 'gap-2',
      md: 'gap-3',
      lg: 'gap-4',
    },
    wrap: {
      true: 'flex-wrap',
      false: 'flex-nowrap',
    },
  },
  defaultVariants: {
    gap: 'md',
    wrap: false,
  },
});

interface SearchFilterBarProps
  extends VariantProps<typeof searchFilterBarVariants> {
  children: React.ReactNode;
  className?: string;
}

const SearchFilterBarRoot: React.FC<SearchFilterBarProps> = ({
  children,
  className,
  gap,
  wrap,
}) => {
  return (
    <div className={cn(searchFilterBarVariants({ gap, wrap }), className)}>
      {children}
    </div>
  );
};

SearchFilterBarRoot.displayName = 'SearchFilterBar';

// ============================================
// Compound Component Export
// ============================================
export const SearchFilterBar = Object.assign(SearchFilterBarRoot, {
  TextSearch,
  ModalSearch,
  DropdownSearch,
  Button: SearchFilterBarButton,
});

export type {
  SearchFilterBarProps,
  TextSearchProps,
  ModalSearchProps,
  DropdownSearchProps,
  FilterOption,
  SearchFilterBarButtonProps,
};
